import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card";
import { useAuth } from "@stores/auth-store";
import { cn } from "@pma/utils/helpers";
import useTranslator from "@hooks/use-translator";
import OrganizationProfile from "./organization-profile";
import { Separator } from "@components/common/separator";
import { CogIcon, FileSpreadsheet, Folders, LocateFixedIcon, Printer, Users } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/common/tabs";
import PrinterSettings from "./printer-settings";
import ProjectSettingsGroup from "./project-settings-group";
import GisSettings from "./gis-settings";
import UsersSettingsGroup from "./user-settings-group";
import { useLocation, useNavigate } from "react-router-dom";
import RolesSettingsTabs from "./financial-roles-settings";
import WorkspaceSettings from "./workspace-settings";

export function CompanySettings() {
  const { authConfig, setTabManager } = useAuth();

  const { translate } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();

  const pathname = location.pathname;

  const allStateTabs = authConfig?.tabs ?? {};
  const settingsActiveTab = allStateTabs ? allStateTabs["settings"] : undefined;
  const activeTabs = settingsActiveTab || {
    main: "workspace",
    sub: undefined,
  };

  return (
    <Card className='relative overflow-hidden'>
      <CardHeader className='flex flex-row items-center border-b'>
        <CardTitle>{translate("Company Settings")}</CardTitle>
      </CardHeader>

      <CardContent className='grid gap-4 p-1 text-sm md:p-6'>
        <OrganizationProfile />

        <Separator />
        <Tabs defaultValue={"settings"} className='w-full p-0'>
          <TabsList className=' flex flex-wrap  h-[100px] md:h-[30px] md:grid w-full max-w-screen-lg grid-cols-6 rounded-full mb-4 bg-transparent'>
            <TabsTrigger
              value='workspace'
              onClick={() => {
                setTabManager({ group: "settings", main: "workspace", sub: undefined });
                navigate(pathname);
              }}
              className={cn(
                "flex items-center bg-transparent rounded-none gap-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-primary",
                activeTabs?.main === "workspace" && "border-b-2",
              )}
            >
              <FileSpreadsheet className='h-4 w-4' />
              {translate("Workspace")}
            </TabsTrigger>
            <TabsTrigger
              value='projects&tasks'
              className={cn(
                "flex items-center bg-transparent rounded-none gap-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-primary",
                activeTabs?.main === "projects&tasks" && "border-b-2",
              )}
              onClick={() => {
                setTabManager({ group: "settings", main: "projects&tasks", sub: undefined });
                navigate(pathname);
              }}
            >
              <Folders className='h-4 w-4' />
              {translate("Projects & Tasks")}
            </TabsTrigger>
            <TabsTrigger
              value='members'
              className={cn(
                "flex items-center bg-transparent rounded-none gap-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-primary",
                activeTabs?.main === "members" && "border-b-2",
              )}
              onClick={() => {
                setTabManager({ group: "settings", main: "members", sub: undefined });
                navigate(pathname);
              }}
            >
              <Users className='h-4 w-4' />
              {translate("Members")}
            </TabsTrigger>
            <TabsTrigger
              value='roles'
              className={cn(
                "flex items-center bg-transparent rounded-none gap-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-primary",
                activeTabs?.main === "roles" && "border-b-2",
              )}
              onClick={() => {
                setTabManager({ group: "settings", main: "roles", sub: "members" });
                navigate(pathname);
              }}
            >
              <CogIcon className='h-4 w-4' />
              {translate("Roles")}
            </TabsTrigger>
            <TabsTrigger
              value='gis'
              onClick={() => {
                setTabManager({ group: "settings", main: "gis", sub: undefined });
                navigate(pathname);
              }}
              className={cn(
                "flex items-center bg-transparent rounded-none gap-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-primary",
                activeTabs?.main === "gis" && "border-b-2",
              )}
            >
              <LocateFixedIcon className='h-4 w-4' />
              {translate("GIS")}
            </TabsTrigger>
            <TabsTrigger
              value='printers'
              className={cn(
                "flex items-center bg-transparent rounded-none gap-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-primary",
                activeTabs?.main === "printers" && "border-b-2",
              )}
              onClick={() => {
                setTabManager({ group: "settings", main: "printers", sub: undefined });
                navigate(pathname);
              }}
            >
              <Printer className='h-4 w-4' />
              {translate("Printers")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value='settings' forceMount>
            <>{activeTabs?.main === "workspace" && <WorkspaceSettings />}</>

            {activeTabs?.main === "projects&tasks" && <ProjectSettingsGroup />}

            {activeTabs?.main === "printers" && <PrinterSettings />}

            {activeTabs?.main === "gis" && <GisSettings />}

            {activeTabs?.main === "members" && <UsersSettingsGroup />}

            {activeTabs?.main === "roles" && <RolesSettingsTabs />}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
